import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { ChakraBox, ChakraFlex, Text } from '@equip.health/ui';
import AppointmentCard from '~/components/common/calendar/AppointmentCard';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';
import { formatShortDate, formatAppointmentShortTime } from './utils';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { useGetAppointmentDetailsQuery } from '~/store/services/appointment';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';
import { INTAKE_SESSION_APPOINTMENT_TYPE_NAMES } from '~/lib/constants/scheduling.constants';
import { AVAILABILITY_WINDOW_WEEKS } from './constants';

const TOP_LEVEL_ID = 'patient-appointments';

interface PatientAppointmentsProps {
  patientId: string;
  admitDate: string;
  inquiryId: string;
}

const PatientAppointments = ({
  patientId,
  admitDate,
  inquiryId,
}: PatientAppointmentsProps) => {
  const startDateISO = useMemo(() => {
    if (!admitDate) return undefined;
    const dt = DateTime.fromISO(admitDate);
    return dt.isValid ? dt.toString() : undefined;
  }, [admitDate]);

  const endDateISO = useMemo(() => {
    if (!startDateISO) return undefined;
    return DateTime.fromISO(startDateISO).plus({ weeks: AVAILABILITY_WINDOW_WEEKS }).toString();
  }, [startDateISO]);

  const { data: appointments, isFetching: isAppointmentsLoading } =
    useGetAppointmentDetailsQuery(
      {
        endDateISO,
        includeTimezoneDetails: true,
        inquiryIds: [inquiryId],
        pageSize: 10,
        patientId,
        startDateISO,
        statuses: [AppointmentStatus.COMPLETED, AppointmentStatus.SCHEDULED],
      },
      {
        skip: !patientId || !startDateISO,
      },
    );

  const sortedAppointments = useMemo(
    () =>
      appointments?.data
        ?.filter((a) =>
          INTAKE_SESSION_APPOINTMENT_TYPE_NAMES.some((intakeType) =>
            a.appointmentTypeLabel?.startsWith(intakeType),
          ),
        )
        ?.toSorted(
          (a, b) =>
            new Date(a.appointmentStartDateTime).getTime() -
            new Date(b.appointmentStartDateTime).getTime(),
        ),
    [appointments],
  );

  if (isAppointmentsLoading) {
    return (
      <ChakraBox paddingTop="30px" width="100%">
        <SkeletonLoaderList
          direction="column"
          id={`${TOP_LEVEL_ID}__placeholder`}
          itemHeight="78px"
        />
      </ChakraBox>
    );
  }

  if (!sortedAppointments?.length) {
    return null;
  }

  return (
    <ChakraFlex direction="column" gap="12px" width="100%">
      <Text id={`${TOP_LEVEL_ID}__header`} variant="bodyMedium" width="100%">
        Appointments
      </Text>
      {sortedAppointments?.map((a: AppointmentDetailResponse) => (
        <AppointmentCard
          avatarProps={{
            name: getPreferredFullName(null, a.host.firstName, a.host.lastName),
            src: a.host.avatar?.url,
            userType: 'provider',
          }}
          endTimeISO={a.appointmentEndDateTime}
          formatDate={formatShortDate}
          formatStartEndTime={formatAppointmentShortTime}
          id={`${TOP_LEVEL_ID}--${a.appointmentExternalId}`}
          key={a.appointmentExternalId}
          startTimeISO={a.appointmentStartDateTime}
          title={a.appointmentTypeLabel}
        />
      ))}
    </ChakraFlex>
  );
};

export default PatientAppointments;
